import React from "react";
import { Routes, Route, Navigate, useParams, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "./SEO";
import Home from "./pages/Home";
import Create from "./pages/Create";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import Edit from "./pages/Edit";
import Success from "./pages/Success";
import Site from "./pages/Site";

const SUPPORTED_LANGUAGES = ["de", "en", "es", "fr", "it", "pt"];
const LANGUAGE_SPECIFIC_PATHS = ["/", "/create", "/privacy", "/terms", "/edit", "/qrcode"];

const RedirectMissingLanguage = ({ defaultLang }) => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const hasLangPrefix = pathSegments.length > 0 && SUPPORTED_LANGUAGES.includes(pathSegments[0]);

  if (!hasLangPrefix && LANGUAGE_SPECIFIC_PATHS.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to={`/${defaultLang}${location.pathname}${location.search}`} replace />;
  }

  return <Outlet />;
};

const LanguageGuard = ({ defaultLang }) => {
  const { lng } = useParams();
  const location = useLocation();
  
  const isValidLang = SUPPORTED_LANGUAGES.includes(lng);

  if (!isValidLang) {
    const remainingPath = location.pathname.replace(/^\/[^/]+/, ""); 
    return <Navigate to={`/${defaultLang}${remainingPath}`} replace />;
  }

  return <Outlet />;
};

const App = () => {
  const { i18n } = useTranslation();
  const defaultLang = i18n.language || "en";

  return (
    <div>
      <SEO />
      <Routes>
        {/* Middleware to enforce language code in URL */}
        <Route path="*" element={<RedirectMissingLanguage defaultLang={defaultLang} />}>
          {/* Language-specific routes inside guard */}
          <Route path=":lng/*" element={<LanguageGuard defaultLang={defaultLang} />}>
            <Route index element={<Home />} />
            <Route path="create" element={<Create />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<TermsOfUse />} />
            <Route path="edit/:userId" element={<Edit />} />
            <Route path="qrcode/:userId" element={<Success />} />
            <Route path="*" element={<Navigate to={`/${defaultLang}`} replace />} />
          </Route>
        </Route>

        {/* Routes without language */}
        <Route path="/u/:slug" element={<Site />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to={`/${defaultLang}`} replace />} />
      </Routes>
    </div>
  );
};

export default App;
